import { matchPath } from "react-router-dom";

/** Which Stop Details page (if any) the path currently points at — used by
 * `DesktopTrackingShell` to tell `MapView` which pin to focus the camera on.
 * Pickup has no id of its own in the URL (see `route/index.tsx`'s `pickup`
 * route), so the caller resolves it from the order summary; dropoff already
 * carries its id in the path. */
export type FocusedStop = { kind: "pickup" } | { kind: "dropoff"; stopId: string };

export type TrackingRouteInfo = {
  trackCode: string;
  isDropoffTracking: boolean;
  focusedStop: FocusedStop | null;
};

/** Paths that look like a bare `/:trackCode` match but aren't order pages —
 * excluded up front so they never get misread as a tracking route. */
const RESERVED_PATHS = new Set(["/", "/not-found", "/expire"]);

/**
 * Most-specific-first mirror of the tracking path patterns mounted in
 * `route/index.tsx`. Needed because `DesktopTrackingShell` wraps `<Routes>`
 * from the outside (so the map can outlive route changes underneath it) and
 * therefore can't rely on `useParams()`, which only resolves inside a
 * matched `<Route>`. Keep in sync if a tracking path ever changes there.
 */
const PATTERNS: Array<{
  pattern: string;
  isDropoffTracking: boolean;
  focusedStop?: (params: Record<string, string | undefined>) => FocusedStop;
}> = [
  { pattern: "/d/details/:trackCode/pickup", isDropoffTracking: true, focusedStop: () => ({ kind: "pickup" }) },
  { pattern: "/details/:trackCode/pickup", isDropoffTracking: false, focusedStop: () => ({ kind: "pickup" }) },
  {
    pattern: "/d/details/:trackCode/dropoff/:stopId",
    isDropoffTracking: true,
    focusedStop: (params) => ({ kind: "dropoff", stopId: params.stopId! }),
  },
  {
    pattern: "/details/:trackCode/dropoff/:stopId",
    isDropoffTracking: false,
    focusedStop: (params) => ({ kind: "dropoff", stopId: params.stopId! }),
  },
  { pattern: "/d/details/:trackCode/*", isDropoffTracking: true },
  { pattern: "/details/:trackCode/*", isDropoffTracking: false },
  { pattern: "/d/:trackCode", isDropoffTracking: true },
  { pattern: "/:trackCode", isDropoffTracking: false },
];

/** Resolves the order `trackCode` + dropoff-tracking mode (+ which Stop
 * Details page is open, if any) for any pathname under the tracking route
 * family, or `null` for Search/NotFound/Expire. */
export function matchTrackingRoute(pathname: string): TrackingRouteInfo | null {
  if (RESERVED_PATHS.has(pathname)) return null;

  for (const { pattern, isDropoffTracking, focusedStop } of PATTERNS) {
    const match = matchPath(pattern, pathname);
    if (match?.params.trackCode) {
      return {
        trackCode: match.params.trackCode,
        isDropoffTracking,
        focusedStop: focusedStop ? focusedStop(match.params) : null,
      };
    }
  }

  return null;
}
